import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, getDocs, query, orderBy, limit, onSnapshot, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  FileStack, Loader2, AlertTriangle, CheckCircle2, RefreshCw,
  ShieldCheck, Hammer, Copy, Trash2, RotateCcw, BadgeCheck, Send, Clock, Server, X, Ban,
} from 'lucide-react';
import WorklistTable from './carbon/WorklistTable';
import CpscAmendProducts from './CpscAmendProducts';

/**
 * CpscDrafts — CHA-170. Certificate draft builder + review, under Imports → CPSC Drafts.
 *
 * Reads assembled drafts straight from Firestore `/cpsc_drafts/{tenantId}/drafts` (rules: tenant-isolated
 * read; writes are server-callable-only). Lets an operator assemble a draft per BV report (assembleDraft),
 * confirm individual needs-confirmation fields (confirmDraftField — anti-rubberstamp, decision #5 Medium),
 * advance the gated 7-state lifecycle (advanceDraft), and supersede a re-uploaded duplicate
 * (markDraftSuperseded — R2, we keep both, never overwrite). NO CPSC submission here (CHA-171).
 *
 * Four-label provenance (decision #4): every field shows where its value came from —
 * BV (report) | Default (tenant cpsc_defaults) | Lookup (mapping table) | Needs confirm (operator owes it).
 */
const s = (v) => String(v ?? '').trim();

// 14-state lifecycle → pill tone + label (mirrors cpscDraft.STATES; +3 submission states CHA-171, +4
// certification states CHA-172 incl. certification_pending).
// Exported as DRAFT_STATUS (CHA-267 PR-3) — Register/Submissions render the SAME labels (§8.2, one vocabulary).
export const STATUS = {
  error: { tone: 'red', label: 'Error', icon: AlertTriangle },
  needs_confirmation: { tone: 'amber', label: 'Needs confirmation', icon: AlertTriangle },
  draft: { tone: 'slate', label: 'Draft', icon: FileStack },
  confirmed: { tone: 'blue', label: 'Confirmed', icon: BadgeCheck },
  ready: { tone: 'green', label: 'Ready', icon: ShieldCheck },
  submitted_validation: { tone: 'blue', label: 'Submitting…', icon: Loader2 },
  awaiting_certification: { tone: 'green', label: 'Awaiting certification', icon: BadgeCheck },
  submission_error: { tone: 'red', label: 'Submission error', icon: AlertTriangle },
  certifying: { tone: 'blue', label: 'Certifying…', icon: Loader2 },
  certified: { tone: 'green', label: 'Certified ✓', icon: CheckCircle2 },
  certification_failed: { tone: 'red', label: 'Certification failed', icon: AlertTriangle },
  certification_pending: { tone: 'amber', label: 'Pending at CPSC', icon: Clock },
  superseded: { tone: 'slate', label: 'Superseded', icon: Copy },
  archived: { tone: 'slate', label: 'Archived', icon: Trash2 },
  dismissed: { tone: 'slate', label: 'Dismissed', icon: Ban },   // CHA-492 — not a certifiable product
};
const TONE = {
  green: 'text-carbon-green-50 border-carbon-green-50 bg-carbon-green-10',
  amber: 'text-carbon-orange-70 border-carbon-orange-40 bg-carbon-white',
  red: 'text-carbon-red-60 border-carbon-red-60 bg-carbon-white',
  blue: 'text-carbon-blue-60 border-carbon-blue-60 bg-carbon-blue-10',
  slate: 'text-carbon-gray-70 border-carbon-gray-30 bg-carbon-white',
};
// Four-label provenance → chip.
const PROV_CHIP = {
  bv: { tone: 'slate', label: 'BV' },
  default: { tone: 'blue', label: 'Default' },
  lookup: { tone: 'green', label: 'Lookup' },
  needs_confirmation: { tone: 'amber', label: 'Needs confirm' },
};
// Per-status operator actions routed through advanceDraft (client mirror of cpscDraft.TRANSITIONS —
// server is authoritative). submitDraft / reopenForEdit are separate callables, rendered specially below.
const ACTIONS = {
  error: [{ action: 'delete', label: 'Delete', icon: Trash2 }, { action: 'dismiss', label: 'Dismiss permanently', icon: Ban }],
  needs_confirmation: [{ action: 'mark_confirmed', label: 'Mark confirmed', icon: BadgeCheck, needsClear: true }, { action: 'delete', label: 'Delete', icon: Trash2 }, { action: 'dismiss', label: 'Dismiss permanently', icon: Ban }],
  draft: [{ action: 'mark_ready', label: 'Mark ready', icon: ShieldCheck, needsValid: true }, { action: 'delete', label: 'Delete', icon: Trash2 }, { action: 'dismiss', label: 'Dismiss permanently', icon: Ban }],
  confirmed: [{ action: 'mark_ready', label: 'Mark ready', icon: ShieldCheck, needsValid: true }, { action: 'reopen', label: 'Reopen', icon: RotateCcw }, { action: 'delete', label: 'Delete', icon: Trash2 }, { action: 'dismiss', label: 'Dismiss permanently', icon: Ban }],
  ready: [{ action: 'reopen', label: 'Reopen', icon: RotateCcw }, { action: 'delete', label: 'Delete', icon: Trash2 }, { action: 'dismiss', label: 'Dismiss permanently', icon: Ban }],
  submitted_validation: [],          // mid-flight — no operator actions
  awaiting_certification: [],         // certify is a special button (CHA-172), rendered below
  submission_error: [{ action: 'delete', label: 'Delete', icon: Trash2 }, { action: 'dismiss', label: 'Dismiss permanently', icon: Ban }], // reopen is reopenForEdit (special)
  certifying: [],                     // mid-flight — no operator actions
  certified: [],                      // TERMINAL legal cert — no actions (CHA-172 decision 2, no rollback)
  certification_failed: [{ action: 'delete', label: 'Delete', icon: Trash2 }, { action: 'dismiss', label: 'Dismiss permanently', icon: Ban }], // retry is a special button (below)
  certification_pending: [],          // in-flight at CPSC — recheck is a special button (below); NEVER re-POSTs
  superseded: [{ action: 'delete', label: 'Delete', icon: Trash2 }, { action: 'dismiss', label: 'Dismiss permanently', icon: Ban }],
  archived: [{ action: 'dismiss', label: 'Dismiss permanently', icon: Ban }],   // CHA-492 — escalate a delete to permanent
  dismissed: [{ action: 'restore', label: 'Restore', icon: RotateCcw }],        // CHA-492 — reversible, but only deliberately
};

// CPSC errorField → draft field key, so a submission error renders inline on the right <Field> row
// (decision 4). Unmapped / opaque errorFields ('general', etc.) route to the draft-level banner.
// CHA-630 — client mirror of cpscDraft.AMEND_IN_PLACE_STATUSES (server is authoritative): an open manual
// amendment not yet sent to CPSC, whose products can still change without a new version.
const AMEND_IN_PLACE = ['error', 'needs_confirmation', 'confirmed', 'draft', 'ready'];
// …and only while it has not been submitted since it was reopened (mirror of cpscDraft.amendInPlaceAllowed).
const tsMs = (t) => (t && typeof t.toMillis === 'function' ? t.toMillis() : typeof t === 'number' ? t : NaN);
export const canAmendInPlace = (d) => d.source === 'MANUAL' && !!d.amendedFromVersionId && AMEND_IN_PLACE.includes(d.status)
  && (d.submittedAt == null || (Number.isFinite(tsMs(d.submittedAt)) && Number.isFinite(tsMs(d.reopenedAt)) && tsMs(d.submittedAt) < tsMs(d.reopenedAt)));
const ERR_FIELD_ALIAS = { 'manufacturer.alternateId': 'manufacturer', 'poc.alternateId': 'poc', 'labs[0].gln': 'labs[0].cpscId' };
const errKey = (errorField) => ERR_FIELD_ALIAS[errorField] || errorField;
const FIELD_KEYS = new Set(['versionId', 'primaryProductId', 'certificateType', 'name', 'color', 'manufactureDate', 'lastTestDate', 'manufacturer', 'poc', 'labs[0].cpscId', 'labs[0].citationCodes']);
const errsFor = (d, fk) => (d.submissionErrors || []).filter((e) => errKey(e.errorField) === fk);
const generalSubErrs = (d) => (d.submissionErrors || []).filter((e) => !FIELD_KEYS.has(errKey(e.errorField)));

function Pill({ tone = 'slate', icon: Icon, children }) {
  return (
    <span className={`inline-flex items-center gap-1 font-plex-mono text-[10px] uppercase tracking-[0.04em] px-1.5 py-0.5 border ${TONE[tone]}`}>
      {Icon ? <Icon size={11} /> : null}{children}
    </span>
  );
}

// Outstanding needs-confirmation fields = provenance 'needs_confirmation' AND not in confirmations.
function outstanding(d) {
  const conf = d.confirmations || {};
  return Object.entries(d.provenance || {}).filter(([f, p]) => p === 'needs_confirmation' && !conf[f]);
}

export default function CpscDrafts({ userProfile } = {}) {
  // Gate matches the sibling CPSC tabs' `canManageCpscSettings` (admin | platform_owner) — platform_admin
  // is intentionally excluded from the CPSC surface (InMoceanDashboard.jsx:506), so don't admit it here
  // either, or the component allows a role the dashboard tab hides (CodeRabbit InMoceanDashboard:672 —
  // resolved by tightening the component to match, NOT loosening the shared dashboard gate).
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner'; // tenantless → must pass tenantId on calls
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const [uploads, setUploads] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 4th-layer review (#207): the FEED error is a separate channel — action failures/nags must never
  // flip the table's honest empty state into a fabricated 'drafts unavailable'.
  const [loadError, setLoadError] = useState(null);
  const [busyId, setBusyId] = useState(null);       // draftId or `${uploadId}:${reportNo}` mid-action
  const [selectedId, setSelectedId] = useState(null); // draft open in the detail drawer (CHA-283)
  const [overrides, setOverrides] = useState({});   // `${draftId}|${field}` → input value
  const [certifyTarget, setCertifyTarget] = useState(null); // draft pending the legal-certify confirm modal (CHA-172)
  const [amendTarget, setAmendTarget] = useState(null); // CHA-630 — certified draft (MANUAL or, since CHA-669, BV) in the Amend products dialog
  // CHA-199 bulk: selected draftIds + in-flight bulk progress + the bulk-certify confirm + a result notice.
  const [selected, setSelected] = useState(() => new Set());
  const [bulk, setBulk] = useState(null);                   // { kind, done, total, report } while a bulk run is active
  const [bulkCertifyOpen, setBulkCertifyOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const [hideDone, setHideDone] = useState(true); // hide certified drafts by default — show the worklist
  // CHA-502 — dismissed drafts are hidden by default too. CHA-492 added the state and guarded every write
  // path but never took them out of the list, which was the operator's original ask ("remove them from the
  // draft list"): 15 of 17 rows were reports already ruled not-a-product. A TOGGLE rather than a hard
  // filter, because Restore must stay reachable — dismissal is deliberately reversible.
  const [hideDismissed, setHideDismissed] = useState(true);
  const [activeBatch, setActiveBatch] = useState(null); // CHA-200 — live server-side batch, subscribed below

  const tenantArg = useCallback((extra = {}) => (isPlatformRole ? { tenantId, ...extra } : { ...extra }), [isPlatformRole, tenantId]);

  // CHA-200: subscribe to the tenant's ACTIVE cert batch so its progress survives navigation (server state,
  // not React state). When the batch finishes/cancels it drops out of the active query → banner clears.
  useEffect(() => {
    if (!isAdmin) return undefined;
    const db = getFirestore();
    const q = query(collection(db, 'cpsc_cert_batches', tenantId, 'batches'), where('status', 'in', ['queued', 'running']), limit(1));
    const unsub = onSnapshot(q, (snap) => {
      setActiveBatch(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
    }, (e) => console.warn('cert-batch subscription failed', e.message));
    return () => unsub();
  }, [isAdmin, tenantId]);

  // CHA-200: launch a SERVER-SIDE cert batch over the selected Awaiting-Certification drafts — survives navigation.
  const createCertBatch = useCallback(async () => {
    const targets = drafts.filter((d) => selected.has(d.draftId) && d.status === 'awaiting_certification').map((d) => d.draftId);
    if (!targets.length) { setError('Select drafts that are "Awaiting Certification" (submitted + validated) to batch-certify.'); return; }
    try {
      const res = await httpsCallable(getFunctions(), 'createBatch')(tenantArg({ draftIds: targets }));
      setNotice(`Background cert batch started — ${res.data.total} draft(s). You can leave this page; it keeps certifying.`);
      setSelected(new Set());
    } catch (e) { setError(e?.message || 'Could not start the batch.'); }
  }, [drafts, selected, tenantArg]);

  const cancelCertBatch = useCallback(async () => {
    if (!activeBatch) return;
    if (!window.confirm('Cancel the background cert batch?\n\nThe certificate currently in-flight finishes (never killed mid-transaction — exactly-once holds); the remaining drafts stop.')) return;
    try { await httpsCallable(getFunctions(), 'cancelBatch')(tenantArg({ batchId: activeBatch.id })); } catch (e) { setError(e?.message || 'Cancel failed.'); }
  }, [activeBatch, tenantArg]);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setLoadError(null);
    try {
      const db = getFirestore();
      const [upSnap, drSnap] = await Promise.all([
        getDocs(query(collection(db, 'cpsc_uploads', tenantId, 'uploads'), orderBy('uploadedAt', 'desc'), limit(50))),
        getDocs(collection(db, 'cpsc_drafts', tenantId, 'drafts')), // all drafts (no cap)
      ]);
      setUploads(upSnap.docs.map((d) => ({ uploadId: d.id, ...d.data() })));
      setDrafts(drSnap.docs.map((d) => ({ draftId: d.id, ...d.data() })));
    } catch (e) {
      // Don't swallow (§8.3) — surface so the operator knows the list didn't load.
      console.error('Failed to load CPSC drafts', e);
      setLoadError('Could not load drafts. Check access and retry.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const call = useCallback(async (name, args, busyKey) => {
    setBusyId(busyKey); setError(null);
    try {
      // submitDraft / certifyDraft / recheckSubmission poll or read CPSC synchronously; the Firebase
      // client default timeout (~70s) is far shorter, so a slow-but-valid round-trip would surface as a
      // client timeout, stale the UI, and invite a duplicate retry. Match the server deadlines (CHA-388
      // raised submit/certify to 600s for the size-scaled poll budget). (Codex P2)
      const longPoll = name === 'submitDraft' || name === 'certifyDraft' || name === 'recheckSubmission';
      const fn = httpsCallable(getFunctions(), name, longPoll ? { timeout: 600000 } : undefined);
      const res = await fn(tenantArg(args));
      await load();
      // CHA-388 (self-review #6) — a pending recheck changes nothing in the list; without this the click
      // is indistinguishable from "nothing happened".
      if (res?.data?.pending) setError(`Still processing at CPSC (status: ${res.data.importStatus || 'unknown'}) — recheck again shortly. Nothing was resubmitted.`);
      return res?.data;
    } catch (e) {
      // Log the full error (stack + action context) before reducing it to UI text (§8.3 / CodeRabbit).
      console.error(`CPSC draft action "${name}" failed`, args, e);
      setError(e?.message || `${name} failed.`);
      return null;
    } finally {
      setBusyId(null);
    }
  }, [tenantArg, load]);

  // Group live (non-archived, non-dismissed) drafts by Report No. for the duplicate badge (R2) + bulk
  // selection. CHA-502 — excluding dismissed here also stops a dismissed sibling inflating a live report's
  // "Duplicate xN" badge, which is what made one dismissed report render as three or four rows.
  const liveDrafts = drafts.filter((d) => d.status !== 'archived' && d.status !== 'dismissed');
  const dismissedDrafts = drafts.filter((d) => d.status === 'dismissed');
  const byReport = liveDrafts.reduce((m, d) => { (m[d.reportNo] = m[d.reportNo] || []).push(d); return m; }, {});
  // Hide the DONE (certified) drafts by default — the list is the worklist of what still needs action.
  const doneCount = liveDrafts.filter((d) => d.status === 'certified').length;
  const shownBase = hideDone ? liveDrafts.filter((d) => d.status !== 'certified') : liveDrafts;
  const shownDrafts = hideDismissed ? shownBase : [...shownBase, ...dismissedDrafts];

  // CHA-199: raw callable invoke (no per-item reload/busyId) for fanning a bulk action out over a selection.
  const invoke = useCallback((name, args) => {
    const longPoll = name === 'submitDraft' || name === 'certifyDraft';
    // CHA-388 — match the raised 600s server deadlines (size-scaled poll budget); a 300s client cap
    // would record false bulk failures while the backend keeps working (Codex P2 / CodeRabbit, PR #295).
    return httpsCallable(getFunctions(), name, longPoll ? { timeout: 600000 } : undefined)(tenantArg(args));
  }, [tenantArg]);

  const toggleSelect = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // CHA-502 (Codex on #374) — prune selections that are no longer selectable. Select a live row, dismiss it
  // from the drawer, and `load()` refreshes: the row is now dismissed and hidden, but its id stayed in
  // `selected`, so the bar read "1 selected" and offered actions that `runBulk` then silently dropped
  // (bulk targets filter on liveDrafts). Pruning fixes the STATE rather than the display, so anything else
  // reading `selected` sees the same invariant: it only ever holds ids that can actually take an action.
  useEffect(() => {
    setSelected((prev) => {
      if (!prev.size) return prev;
      // Derived from `drafts` rather than `liveDrafts`: the latter is rebuilt every render, so using it as
      // a dependency churns identity on each pass. `drafts` only changes when load() returns.
      const ok = new Set(drafts.filter((d) => d.status !== 'archived' && d.status !== 'dismissed').map((d) => d.draftId));
      const next = new Set([...prev].filter((id) => ok.has(id)));
      return next.size === prev.size ? prev : next;   // same Set reference when unchanged — React bails out
    });
  }, [drafts]);
  // Eligibility per bulk action — mirrors the per-draft controls so bulk never forces an illegal transition.
  const bulkEligible = (d, kind) => kind === 'mark_ready' ? (['draft', 'confirmed'].includes(d.status) && d.validation?.ok)
    : kind === 'submit' ? d.status === 'ready'
      : kind === 'certify' ? d.status === 'awaiting_certification' : false;

  // Fan a bulk action out over the eligible selected drafts, SEQUENTIALLY (each submit/certify polls CPSC for
  // minutes; parallel would blow the deadline). Each draft still runs its own gated callable — bulk never
  // weakens the state machine, the CHA-197 all-N legal gate, or exactly-once. Reloads once at the end.
  const runBulk = useCallback(async (kind) => {
    const targets = liveDrafts.filter((d) => selected.has(d.draftId) && bulkEligible(d, kind));
    if (!targets.length) { setError(`None of the selected drafts can ${kind === 'mark_ready' ? 'be marked ready' : kind}.`); return; }
    setError(null); setNotice(null);
    const nameFor = { mark_ready: 'advanceDraft', submit: 'submitDraft', certify: 'certifyDraft' }[kind];
    let ok = 0; let already = 0; const fails = [];
    for (let i = 0; i < targets.length; i++) {
      const d = targets[i];
      setBulk({ kind, done: i, total: targets.length, report: d.reportNo });
      try { await invoke(nameFor, kind === 'mark_ready' ? { draftId: d.draftId, action: 'mark_ready' } : { draftId: d.draftId }); ok += 1; }
      catch (e) {
        const msg = e?.message || 'failed';
        // A draft that's already certified / mid-certifying is the desired end state (a stale selection re-run,
        // e.g. after a page-navigation interrupted the batch) — count it as done, not a failure.
        if (/cannot certify a "(certified|certifying)"/i.test(msg)) { already += 1; }
        else { console.error(`bulk ${kind} ${d.reportNo} failed`, e); fails.push(`${d.reportNo}: ${msg}`); }
      }
    }
    setBulk(null); setSelected(new Set()); await load();
    setNotice(`Bulk ${kind === 'mark_ready' ? 'mark-ready' : kind}: ${ok} ok${already ? ` · ${already} already done` : ''}${fails.length ? ` · ${fails.length} failed — ${fails.slice(0, 3).join(' | ')}` : ''}.`);
  }, [selected, liveDrafts, invoke, load]);

  // Assemble EVERY not-yet-assembled report across all uploads, sequentially (operator: bulk-assemble instead
  // of one at a time). Each still runs its own gated assembleDraft; reloads once at the end.
  const assembleAll = async () => {
    const pending = uploads.flatMap((u) => (u.reports || [])
      .filter((r) => !hasDraftFor(u.uploadId, r.reportNo))
      .map((r) => ({ uploadId: u.uploadId, reportNo: r.reportNo })));
    if (!pending.length) { setError('Nothing to assemble — every report already has a draft.'); return; }
    setError(null); setNotice(null);
    let ok = 0; const fails = [];
    for (let i = 0; i < pending.length; i++) {
      setBulk({ kind: 'assemble', done: i, total: pending.length, report: pending[i].reportNo });
      try { await invoke('assembleDraft', pending[i]); ok += 1; }
      catch (e) { console.error('bulk assemble failed', pending[i], e); fails.push(pending[i].reportNo); }
    }
    setBulk(null); await load();
    setNotice(`Assembled ${ok}/${pending.length} report(s)${fails.length ? ` · ${fails.length} failed` : ''}.`);
  };

  if (!isAdmin) {
    return (
      <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">
        CPSC Drafts is restricted to administrators.
      </div>
    );
  }

  // Only LIVE drafts count as "assembled" — otherwise a deleted/archived draft would show the upload row
  // as done forever and block rebuilding from it (CodeRabbit CpscDrafts.jsx:135).
  // CHA-492 — `archived` is the ONLY status excluded here, so a `dismissed` draft already counts as
  // assembled and the upload row is not re-offered. That is load-bearing, not incidental: it is what stops
  // the archive→re-assemble→archive loop, and adding `dismissed` to this exclusion would restore it. The
  // server enforces the same split independently (NO_REBUILD_STATUSES in assembleDraft + saveManualCoc).
  const hasDraftFor = (uploadId, reportNo) => drafts.some((d) => d.uploadId === uploadId && d.reportNo === reportNo && d.status !== 'archived');

  // CHA-283 — draft detail rendered as WorklistTable drawer content (the
  // chrome — overlay, backdrop, Escape, focus restore — is the primitive's).
  // Everything inside is the pre-migration expanded-row detail: same banners,
  // same four-label provenance fields, same gated lifecycle actions through
  // the same callables (advanceDraft / submitDraft / certifyDraft / …).
  const renderDraftDrawer = (d) => {
    const st = STATUS[d.status] || STATUS.draft;
    const out = outstanding(d);
    const dupes = (byReport[d.reportNo] || []).length;
    return (
      <>
        {/* Header (white) */}
        <div className="bg-carbon-white border-b border-carbon-border px-5 py-4 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-plex-mono text-lg font-semibold text-carbon-ink">Report {d.reportNo}</span>
              {d.styleNo && <span className="text-xs text-carbon-gray-50">{d.styleNo}</span>}
              <Pill tone={st.tone} icon={st.icon}>{st.label}</Pill>
              {dupes > 1 && <Pill tone="amber" icon={Copy}>Duplicate ×{dupes}</Pill>}
              {out.length > 0 && <Pill tone="amber">{out.length} to confirm</Pill>}
            </div>
            <div className="font-plex-mono text-[10px] text-carbon-gray-40 truncate mt-1" title={d.uploadId}>{d.uploadId}</div>
          </div>
          <button type="button" onClick={() => setSelectedId(null)} disabled={busyId === d.draftId} aria-label="Close" className="text-carbon-gray-50 hover:text-carbon-ink p-1 disabled:opacity-50 shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Body (gray-10, scrollable) */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* Action errors surface INSIDE the drawer too — the page-level banner
              sits behind this full-screen overlay, so without this a failed
              drawer action (advanceDraft / submitDraft / certifyDraft / …)
              would be invisible (§6.3 self-review P1). */}
          {error && (
            <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1.5 flex items-start gap-1">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
          {/* Validation errors */}
          {d.validation && !d.validation.ok && (
            <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1.5 mt-2">
              <div className="font-medium flex items-center gap-1"><AlertTriangle size={12} /> Validation</div>
              <ul className="list-disc ml-4 mt-0.5">{d.validation.errors.map((e) => <li key={e.field}>{e.message}</li>)}</ul>
            </div>
          )}

          {/* CPSC submission errors that don't map to a specific field (decision 4 fallback) */}
          {generalSubErrs(d).length > 0 && (
            <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1.5 mt-2">
              <div className="font-medium flex items-center gap-1"><Send size={12} /> CPSC submission error</div>
              <ul className="list-disc ml-4 mt-0.5">{generalSubErrs(d).map((e, i) => <li key={i}>{e.errorMessage}{e.errorCode ? ` [${e.errorCode}]` : ''}</li>)}</ul>
            </div>
          )}

          {/* CPSC certification errors (CHA-172) — the doCertify=TRUE round-trip failed */}
          {(d.certificationErrors || []).length > 0 && (
            <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1.5 mt-2">
              <div className="font-medium flex items-center gap-1"><AlertTriangle size={12} /> CPSC certification error{d.certifyAttempts ? ` (attempt ${d.certifyAttempts}/3)` : ''}</div>
              <ul className="list-disc ml-4 mt-0.5">{(d.certificationErrors || []).map((e, i) => <li key={i}>{e.errorMessage}{e.errorCode ? ` [${e.errorCode}]` : ''}</li>)}</ul>
            </div>
          )}

          {/* Certification pending — the certify request reached CPSC but its outcome isn't confirmed
              yet (CHA-172, Codex P1). Recheck reconciles; the system NEVER auto-resubmits. */}
          {d.status === 'certification_pending' && (
            <div className="text-xs text-carbon-orange-70 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-orange-40 px-2 py-1.5 mt-2 flex items-start gap-1">
              <Clock size={12} className="mt-0.5 shrink-0" />
              <span>{s(d.certificationPendingNote) || 'Certification is in-flight at CPSC — recheck to reconcile. Do not resubmit.'}</span>
            </div>
          )}

          {/* Certified — the legal CoC exists at CPSC (CHA-172, terminal) */}
          {d.status === 'certified' && (
            <div className="text-xs text-carbon-gray-80 bg-carbon-green-10 border border-carbon-green-50 px-2 py-1.5 mt-2 flex items-start gap-1">
              <BadgeCheck size={12} className="mt-0.5 shrink-0" />
              <span>Certified with CPSC — a legally-binding Certificate of Conformity now exists for version <strong>{s(d.versionId)}</strong>{d.certificateId ? ` (cert ${s(d.certificateId)})` : ''}. This is terminal; amendments require a new certificate version.</span>
            </div>
          )}

          {/* CPSC payload preview + four-label provenance (+ inline submission errors per field) */}
          <div className="mt-2 grid grid-cols-1 gap-1.5 text-xs">
            <Field label="Version ID" value={d.versionId} prov={d.provenance?.versionId} fieldErrors={errsFor(d, 'versionId')} />
            {/* CHA-197: a CoC can carry N products — show the WHOLE read-only productList (one CoC per
                report). Anti-rubberstamp confirm applies to the list as a whole (keyed 'primaryProductId'). */}
            <Field label={`Product IDs (${(d.productList && d.productList.length ? d.productList : [d.primaryProductId].filter(Boolean)).length})`} value={(d.productList && d.productList.length ? d.productList : [d.primaryProductId].filter(Boolean)).join(', ')} prov={d.provenance?.primaryProductId} d={d} field="primaryProductId" out={out} call={call} busyId={busyId} overrides={overrides} setOverrides={setOverrides} fieldErrors={[...errsFor(d, 'primaryProductId'), ...errsFor(d, 'productList')]} />
            <Field label="Certificate type" value={d.certificateType} prov={d.provenance?.certificateType} fieldErrors={errsFor(d, 'certificateType')} />
            <Field label="Name" value={d.name} prov={d.provenance?.name} fieldErrors={errsFor(d, 'name')} />
            {/* CHA-499 — editable ONLY once CPSC has rejected it: reopenForEdit marks provenance.color = NEEDS for
                          the fields CPSC objected to, and `out` gates confirmability on exactly that. Before a rejection
                          `color` has no provenance entry, so this renders display-only as it always did. */}
                      <Field label="Color" value={d.color} prov={d.provenance?.color} d={d} field="color" out={out} call={call} busyId={busyId} overrides={overrides} setOverrides={setOverrides} fieldErrors={errsFor(d, 'color')} />
            <Field label="Manufacture date" value={d.manufactureDate} prov={d.provenance?.manufactureDate} d={d} field="manufactureDate" out={out} call={call} busyId={busyId} overrides={overrides} setOverrides={setOverrides} fieldErrors={errsFor(d, 'manufactureDate')} />
            <Field label="Last test date" value={d.lastTestDate} prov={d.provenance?.lastTestDate} d={d} field="lastTestDate" out={out} call={call} busyId={busyId} overrides={overrides} setOverrides={setOverrides} fieldErrors={errsFor(d, 'lastTestDate')} />
            <Field label="Manufacturer" value={d.manufacturer ? `${s(d.manufacturer.name)} (${s(d.manufacturer.alternateId)})` : '—'} prov={d.provenance?.manufacturer} d={d} field="manufacturer" out={out} call={call} busyId={busyId} overrides={overrides} setOverrides={setOverrides} object fieldErrors={errsFor(d, 'manufacturer')} />
            <Field label="POC" value={d.poc ? `${s(d.poc.name)} (${s(d.poc.alternateId)})` : '—'} prov={d.provenance?.poc} d={d} field="poc" out={out} call={call} busyId={busyId} overrides={overrides} setOverrides={setOverrides} object fieldErrors={errsFor(d, 'poc')} />
            <Field label="Lab" value={s(d.labs?.[0]?.labDisplay) || s(d.labs?.[0]?.cpscId) || '—'} prov={d.provenance?.['labs[0].cpscId']} d={d} field="labs[0].cpscId" out={out} call={call} busyId={busyId} overrides={overrides} setOverrides={setOverrides} placeholder="4-digit CPSC Lab ID" fieldErrors={errsFor(d, 'labs[0].cpscId')} />
            <Field label="Citations" value={(d.labs?.[0]?.citationCodes || []).join(', ') || '—'} prov={d.provenance?.['labs[0].citationCodes']} d={d} field="labs[0].citationCodes" out={out} call={call} busyId={busyId} overrides={overrides} setOverrides={setOverrides} placeholder="e.g. 1610" fieldErrors={errsFor(d, 'labs[0].citationCodes')} />
          </div>

        </div>

        {/* Footer (white) — lifecycle actions, verbatim pre-migration */}
        <div className="bg-carbon-white border-t border-carbon-border px-5 py-3">
          {/* Lifecycle actions */}
          <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-carbon-gray-10">
            {(ACTIONS[d.status] || []).map((a) => {
              const blocked = (a.needsClear && out.length > 0) || (a.needsValid && (!d.validation?.ok || out.length > 0));
              return (
                <button
                  key={a.action}
                  onClick={() => call('advanceDraft', { draftId: d.draftId, action: a.action }, d.draftId)}
                  disabled={busyId === d.draftId || blocked}
                  title={blocked ? 'Resolve all needs-confirmation fields / validation first' : ''}
                  className={`flex items-center gap-1 px-2 py-1 text-xs border disabled:opacity-50 ${a.action === 'delete' ? 'border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10' : 'border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10'}`}
                >
                  {busyId === d.draftId ? <Loader2 size={11} className="animate-spin" /> : <a.icon size={11} />} {a.label}
                </button>
              );
            })}
            {/* CHA-492 (CodeRabbit on #367) — `dismissed` must be excluded too: markDraftSuperseded rejects it
                server-side, so rendering the button only offers the operator a click that is guaranteed to fail. */}
            {dupes > 1 && !['superseded', 'archived', 'dismissed', 'submitted_validation', 'awaiting_certification', 'certifying', 'certification_pending', 'certified'].includes(d.status) && (
              <button
                onClick={() => call('markDraftSuperseded', { draftId: d.draftId }, d.draftId)}
                disabled={busyId === d.draftId}
                title="Mark this draft superseded by a newer upload of the same Report No."
                className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-orange-40 text-carbon-orange-70 hover:bg-carbon-gray-10 disabled:opacity-50"
              >
                <Copy size={11} /> Supersede
              </button>
            )}
            {/* Submit to CPSC — per-draft, doCertify=FALSE (submitDraft callable, decisions 1/2/6) */}
            {d.status === 'ready' && (
              <button
                onClick={() => call('submitDraft', { draftId: d.draftId }, d.draftId)}
                disabled={busyId === d.draftId}
                title="Submit this draft to CPSC for validation (doCertify=FALSE — does NOT certify)"
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50"
              >
                {busyId === d.draftId ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Submit to CPSC
              </button>
            )}
            {/* CHA-388 — ambiguous submission failure (importId minted, outcome unread): reconcile against
                CPSC's record instead of resubmitting (a re-POST of the same versionId mints a duplicate).
                Only the ambiguous classes qualify; definitive validation errors go through Reopen. */}
            {d.status === 'submission_error' && d.importId
              && (d.submissionErrors || []).length > 0
              && (d.submissionErrors || []).every((e) => ['cpsc_pending', 'cpsc_status_error', 'cpsc_log_error', 'cpsc_timeout'].includes(e.errorCode)) && (
              <button
                onClick={() => call('recheckSubmission', { draftId: d.draftId }, d.draftId)}
                disabled={busyId === d.draftId}
                title="The import reached CPSC but its outcome wasn't read — reconcile against CPSC's record (never resubmits)"
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50"
              >
                {busyId === d.draftId ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Recheck submission
              </button>
            )}
            {/* Manual reopen after a submission error (decision 5, anti-rubberstamp) */}
            {d.status === 'submission_error' && (
              <button
                onClick={() => call('reopenForEdit', { draftId: d.draftId }, d.draftId)}
                disabled={busyId === d.draftId}
                title="Reopen this draft to fix the CPSC errors and re-submit"
                className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10 disabled:opacity-50"
              >
                {busyId === d.draftId ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} Reopen to edit
              </button>
            )}
            {d.status === 'submitted_validation' && (
              <span className="font-plex-mono text-[10.5px] text-carbon-blue-60 flex items-center gap-1 ml-auto"><Loader2 size={11} className="animate-spin" /> Submitting to CPSC…</span>
            )}
            {/* CHA-208 amend: reopen a certified CoC to add missed products + re-certify as a new version */}
            {/* CHA-630 — MANUAL drafts amend through the products dialog (additive; removals ticked one by one).
                Reopen + re-upload replaced the whole list and dropped certified products three times in prod. */}
            {/* CHA-669 — BV drafts too: a certified BV CoC needs the dialog to correct its type/citations/colours
                (and its mis-parsed product ids). In-place editing stays MANUAL-only via canAmendInPlace. */}
            {(d.status === 'certified' || canAmendInPlace(d)) && (
              <button
                onClick={() => { setError(null); setAmendTarget(d); }}
                disabled={busyId === d.draftId}
                title={d.status === 'certified' ? 'Correct the products, certificate type, citations or colours of this certified CoC and re-certify as a new version' : 'Change the products on this open amendment (same version — mark it ready again afterwards)'}
                className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-orange-40 text-carbon-orange-70 hover:bg-carbon-gray-10 disabled:opacity-50"
              >
                {busyId === d.draftId ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} Amend products
              </button>
            )}
            {/* CHA-669 — BV's bare "Reopen to amend" button is gone: the Amend dialog above covers it, including a reopen
                with no edits (to re-drop the Report-Level export). Two reopen actions on one card was Codex L3 P2 on #501. */}
            {/* Certify — doCertify=TRUE (legally binding). Single-step (decision 1) but gated behind a
                confirm modal because it mints a real CoC. Opens the modal; certifyDraft is the callable. */}
            {d.status === 'awaiting_certification' && (
              <button
                onClick={() => setCertifyTarget(d)}
                disabled={busyId === d.draftId}
                title="Certify this product with CPSC (doCertify=TRUE — creates a legally-binding Certificate of Conformity)"
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-carbon-green-50 text-white hover:opacity-90 disabled:opacity-50"
              >
                {busyId === d.draftId ? <Loader2 size={11} className="animate-spin" /> : <BadgeCheck size={11} />} Certify with CPSC
              </button>
            )}
            {d.status === 'certifying' && (
              <span className="font-plex-mono text-[10.5px] text-carbon-blue-60 flex items-center gap-1 ml-auto"><Loader2 size={11} className="animate-spin" /> Certifying with CPSC…</span>
            )}
            {/* Recheck an in-flight certification (Codex P1) — reuses certifyDraft, which detects the
                pending state and RECONCILES only (never re-POSTs doCertify=TRUE). */}
            {d.status === 'certification_pending' && (
              <button
                onClick={() => call('certifyDraft', { draftId: d.draftId }, d.draftId)}
                disabled={busyId === d.draftId}
                title="Recheck the certification status at CPSC (reconciles only — never resubmits)"
                className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-orange-40 text-carbon-orange-70 hover:bg-carbon-gray-10 disabled:opacity-50"
              >
                {busyId === d.draftId ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Recheck status
              </button>
            )}
            {/* Retry a failed certification — certifyDraft drives certification_failed → certifying
                server-side (retry_certify), counter-gated ≤3. After 3, supersede + rebuild. */}
            {d.status === 'certification_failed' && (
              <button
                onClick={() => setCertifyTarget(d)}
                disabled={busyId === d.draftId || (d.certifyAttempts || 0) >= 3}
                title={(d.certifyAttempts || 0) >= 3 ? 'Max 3 attempts reached — supersede and rebuild' : 'Retry certification with CPSC'}
                className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-orange-40 text-carbon-orange-70 hover:bg-carbon-gray-10 disabled:opacity-50"
              >
                {busyId === d.draftId ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} Retry certify
              </button>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="font-plex text-carbon-ink space-y-5 max-w-3xl">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-carbon-ink">CPSC eFiling — Drafts</h3>
          <p className="text-sm text-carbon-gray-70">
            Assemble a CPSC certificate draft per BV report, review the four-label provenance, confirm any
            fields the lookups couldn&apos;t resolve, mark drafts ready, submit to CPSC for validation
            (doCertify=FALSE), then certify a validated draft (doCertify=TRUE — a legally-binding
            Certificate of Conformity).
          </p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-50">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
        </button>
      </div>

      {isPlatformRole && (
        <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-2">
          Acting as <strong>{role}</strong> against tenant: <strong>{tenantId}</strong>
        </div>
      )}
      {error && (
        <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* CHA-200: live server-side cert batch — progress survives leaving the page */}
      {activeBatch && (
        <div className="text-xs bg-carbon-green-10 border border-carbon-green-50 px-3 py-2 flex items-center gap-2">
          <Server size={14} className="text-carbon-green-50 shrink-0" />
          <span className="text-carbon-gray-80">
            Background cert batch <strong>{activeBatch.status}</strong> — <strong>{activeBatch.doneCount || 0}/{activeBatch.total}</strong> certified{activeBatch.failedCount ? `, ${activeBatch.failedCount} failed` : ''}
            {activeBatch.status === 'running' && activeBatch.currentReport ? ` · certifying ${activeBatch.currentReport}…` : ''}. Keeps running if you leave this page.
          </span>
          <div className="flex-1" />
          {activeBatch.status === 'running' && <Loader2 size={12} className="animate-spin text-carbon-green-50" />}
          <button onClick={cancelCertBatch} className="flex items-center gap-1 px-2 py-0.5 border border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10"><X size={11} /> Cancel</button>
        </div>
      )}

      {/* Assemble drafts from recent uploads */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-plex-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-carbon-gray-50">Build from uploads</h4>
          {(() => {
            const pendingCount = uploads.reduce((n, u) => n + (u.reports || []).filter((r) => !hasDraftFor(u.uploadId, r.reportNo)).length, 0);
            if (!pendingCount) return null;
            const asm = bulk?.kind === 'assemble';
            return (
              <button onClick={assembleAll} disabled={!!bulk} className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50">
                {asm ? <Loader2 size={11} className="animate-spin" /> : <Hammer size={11} />}
                {asm ? `Assembling ${bulk.done}/${bulk.total}…` : `Assemble all (${pendingCount})`}
              </button>
            );
          })()}
        </div>
        {!uploads.length && !loading && <p className="text-sm text-carbon-gray-50">No BV uploads yet — ingest one under BV Upload first.</p>}
        {uploads.map((u) => {
          const upReports = u.reports || [];
          // CHA-199: when "hide done" is on, drop the already-assembled reports (and fully-done uploads) so the
          // build list is just what still needs assembling — no wall of green "Assembled" rows.
          const visible = hideDone ? upReports.filter((r) => !hasDraftFor(u.uploadId, r.reportNo)) : upReports;
          if (hideDone && !visible.length) return null;
          return (
          <div key={u.uploadId} className="bg-carbon-white border border-carbon-border p-3">
            <div className="font-plex-mono text-[11px] text-carbon-gray-50 mb-2 truncate">{s(u.fileName) || 'bv-export.xlsx'} · {upReports.length} report{upReports.length === 1 ? '' : 's'}{hideDone && visible.length < upReports.length ? ` · ${visible.length} to assemble` : ''}</div>
            <div className="space-y-1">
              {visible.map((r) => {
                const done = hasDraftFor(u.uploadId, r.reportNo);
                const key = `${u.uploadId}:${r.reportNo}`;
                return (
                  <div key={key} className="flex items-center gap-2 text-xs">
                    <span className="font-plex-mono font-medium text-carbon-ink">Report {r.reportNo}</span>
                    {r.styleNo && <span className="text-carbon-gray-50">· {r.styleNo}</span>}
                    {r.summary?.needsConfirmation > 0 && <span className="text-carbon-orange-70">· {r.summary.needsConfirmation} to confirm</span>}
                    <div className="flex-1" />
                    {done ? (
                      <Pill tone="green" icon={CheckCircle2}>Assembled</Pill>
                    ) : (
                      <>
                      {/* CHA-502 — dismiss a report WITHOUT keeping it in the queue. Deliberately composed
                          from the two existing guarded callables rather than a fifth write path (§8.2):
                          assemble, then dismiss. The transient draft is the audit record of the decision,
                          and it never reaches the operator's list because dismissed rows are hidden. */}
                      <button
                        onClick={async () => {
                          const made = await call('assembleDraft', { uploadId: u.uploadId, reportNo: r.reportNo }, key);
                          // Only dismiss what assembleDraft actually created — on failure `call` returns
                          // undefined and surfaces the error, and dismissing a guessed id would be worse
                          // than doing nothing.
                          if (made?.draftId) await call('advanceDraft', { draftId: made.draftId, action: 'dismiss' }, key);
                        }}
                        disabled={busyId === key}
                        title="Not a certifiable product — dismiss this report so it stops appearing. Reversible via Restore."
                        className="flex items-center gap-1 px-2 py-0.5 border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-50"
                      >
                        <Ban size={11} /> Dismiss
                      </button>
                      <button
                        onClick={() => call('assembleDraft', { uploadId: u.uploadId, reportNo: r.reportNo }, key)}
                        disabled={busyId === key}
                        className="flex items-center gap-1 px-2 py-0.5 font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50"
                      >
                        {busyId === key ? <Loader2 size={11} className="animate-spin" /> : <Hammer size={11} />} Assemble
                      </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </section>

      {/* Drafts */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-plex-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-carbon-gray-50">Drafts</h4>
          <div className="flex items-center gap-3">
          {dismissedDrafts.length > 0 && (
            <button onClick={() => setHideDismissed((v) => !v)} aria-pressed={hideDismissed} className="text-xs text-carbon-gray-70 hover:text-carbon-ink underline decoration-dotted">
              {hideDismissed ? `Show ${dismissedDrafts.length} dismissed` : `Hide ${dismissedDrafts.length} dismissed`}
            </button>
          )}
          {doneCount > 0 && (
            <button onClick={() => setHideDone((v) => !v)} aria-pressed={hideDone} className="text-xs text-carbon-gray-70 hover:text-carbon-ink underline decoration-dotted">
              {hideDone ? `Show ${doneCount} certified` : `Hide ${doneCount} certified`}
            </button>
          )}
          </div>
        </div>
        {/* CHA-199 bulk bar — select-all + fan a status-appropriate action over the selection */}
        {shownDrafts.length > 0 && (() => {
          // CHA-502 (Codex on #374) — a REVEALED dismissed row must not be selectable. Every bulk action
          // already filters on `liveDrafts`, which excludes dismissed, so no wrong action could fire — but
          // select-all would tick 15 rows and the bar would read "15 selected" while every action was a
          // no-op. Restore is the only legal action on a dismissed draft and it is per-row.
          const selectable = shownDrafts.filter((d) => d.status !== 'dismissed').map((d) => d.draftId);
          const allSelected = selectable.length > 0 && selectable.every((id) => selected.has(id));
          return (
            <div className="flex items-center gap-2 text-xs bg-carbon-gray-10 border border-carbon-border px-3 py-1.5">
              <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(selectable))} aria-label="Select all drafts" disabled={!!bulk} />
              <span className="text-carbon-gray-70">{selected.size ? `${selected.size} selected` : 'Select all'}</span>
              {/* CHA-502 — say so rather than silently offering fewer rows than are on screen. */}
              {hideDismissed === false && dismissedDrafts.length > 0 && (
                <span className="text-carbon-gray-50">· {dismissedDrafts.length} dismissed not selectable</span>
              )}
              <div className="flex-1" />
              {bulk && <span className="text-carbon-gray-70 flex items-center gap-1"><Loader2 size={11} className="animate-spin" />{bulk.kind === 'mark_ready' ? 'Marking ready' : bulk.kind === 'submit' ? 'Submitting' : 'Certifying'} {bulk.done + 1}/{bulk.total} · {bulk.report}…</span>}
              {selected.size > 0 && !bulk && (
                <>
                  <button onClick={() => runBulk('mark_ready')} className="flex items-center gap-1 px-2 py-1 border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-white"><ShieldCheck size={11} /> Mark ready</button>
                  <button onClick={() => runBulk('submit')} className="flex items-center gap-1 px-2 py-1 border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-white"><Send size={11} /> Submit</button>
                  <button onClick={() => setBulkCertifyOpen(true)} className="flex items-center gap-1 px-2 py-1 border border-carbon-green-50 text-carbon-green-50 hover:bg-carbon-green-10"><BadgeCheck size={11} /> Certify</button>
                  <button onClick={createCertBatch} disabled={!!activeBatch} title={activeBatch ? 'A background batch is already running' : 'Certify these in the background — survives leaving the page'} className="flex items-center gap-1 px-2 py-1 font-semibold bg-carbon-green-50 text-white hover:opacity-90 disabled:opacity-50"><Server size={11} /> Batch (background)</button>
                </>
              )}
            </div>
          );
        })()}
        {notice && <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-1.5">{notice}</div>}
        {/* CHA-283 — the drafts worklist renders through the canonical
            WorklistTable. Row anatomy: bulk checkbox | report + pills | upload,
            plus the primitive's STATUS chip column; clicking a row opens the
            detail drawer (was the expanded accordion) — same fields, same
            gated actions through the same callables. */}
        <WorklistTable
          columns={[
            {
              key: 'select',
              width: '28px',
              // CHA-502 — no checkbox on a dismissed row: it cannot take any bulk action, and rendering
              // one invites a selection that silently does nothing.
              render: (d) => (d.status === 'dismissed' ? null : (
                <input
                  type="checkbox"
                  checked={selected.has(d.draftId)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(d.draftId)}
                  aria-label={`Select report ${d.reportNo}`}
                />
              )),
            },
            {
              key: 'report',
              label: 'Report',
              render: (d) => {
                const out = outstanding(d);
                const dupes = (byReport[d.reportNo] || []).length;
                return (
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="font-plex-mono font-medium text-sm text-carbon-ink">Report {d.reportNo}</span>
                    {d.styleNo && <span className="text-xs text-carbon-gray-50">{d.styleNo}</span>}
                    {dupes > 1 && <Pill tone="amber" icon={Copy}>Duplicate ×{dupes}</Pill>}
                    {out.length > 0 && <Pill tone="amber">{out.length} to confirm</Pill>}
                  </div>
                );
              },
            },
            {
              key: 'upload',
              label: 'Upload',
              width: 'max-content',
              align: 'right',
              render: (d) => (
                <span className="text-[10px] text-carbon-gray-40 font-plex-mono truncate max-w-[140px] inline-block" title={d.uploadId}>{d.uploadId}</span>
              ),
            },
          ]}
          rows={shownDrafts}
          rowKey={(d) => d.draftId}
          rowTestId="draft-row"
          statusChipOf={(d) => {
            const st = STATUS[d.status] || STATUS.draft;
            return { label: st.label, icon: st.icon, className: TONE[st.tone] };
          }}
          onRowClick={(d) => setSelectedId(d.draftId)}
          selectedKey={selectedId}
          loading={loading}
          error={loadError ? { what: loadError, emptyText: 'Drafts unavailable — see the notice above.' } : null}
          empty={{
            title: hideDone && doneCount ? 'All drafts are certified.' : 'No drafts yet.',
            detail: hideDone && doneCount ? 'Nothing left to work.' : 'Assemble one above.',
          }}
          drawer={{
            width: 560,
            ariaLabelOf: (d) => `Draft report ${d.reportNo}`,
            onClose: () => setSelectedId(null),
            // Also hold the drawer while the legal-certify confirm modal is up —
            // Escape aimed at the topmost modal must not close the drawer under it.
            closeDisabled: (!!busyId && busyId === selectedId) || !!certifyTarget,
            render: renderDraftDrawer,
          }}
        />
      </section>

      {amendTarget && (
        <CpscAmendProducts
          draft={amendTarget}
          busy={busyId === amendTarget.draftId}
          error={error}
          onCancel={() => setAmendTarget(null)}
          onConfirm={async (amendment) => {
            const id = amendTarget.draftId;
            const res = await call('reopenCertified', { draftId: id, ...amendment }, id); // CHA-669 — may carry certificateType / citationCodes / productColors
            if (res) setAmendTarget(null); // on a refusal keep the dialog open — the error banner names the reason
          }}
        />
      )}

      {/* Legal-certification confirm modal (CHA-172, decision 1 — single-step, but gated behind an
          explicit confirm because doCertify=TRUE mints a binding Certificate of Conformity). */}
      {certifyTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="cpsc-certify-title" aria-describedby="cpsc-certify-description">
          <div className="font-plex bg-carbon-white border-t-[3px] border-t-carbon-blue-60 border border-carbon-border max-w-md w-full p-4 space-y-3">
            <div id="cpsc-certify-title" className="flex items-center gap-2 text-carbon-ink font-semibold">
              <BadgeCheck size={16} className="text-carbon-green-50" /> Certify with CPSC
            </div>
            <p id="cpsc-certify-description" className="text-sm text-carbon-gray-70">
              This submits <strong>doCertify=TRUE</strong> and creates a <strong>legally-binding Certificate
              of Conformity</strong> for {(certifyTarget.productList && certifyTarget.productList.length > 1)
                ? <><strong>{certifyTarget.productList.length} products</strong> (<span className="font-mono">{certifyTarget.productList.join(', ')}</span>)</>
                : <>product <strong>{s(certifyTarget.primaryProductId)}</strong></>} (version{' '}
              <strong>{s(certifyTarget.versionId)}</strong>). By certifying, <strong>{tenantId}</strong> attests
              the product was tested per the cited standard and the test data is accurate. This certification is
              binding for CBP entry and cannot be rolled back.
            </p>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setCertifyTarget(null)}
                disabled={busyId === certifyTarget.draftId}
                autoFocus
                className="px-3 py-1.5 text-xs border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => { const id = certifyTarget.draftId; setCertifyTarget(null); call('certifyDraft', { draftId: id }, id); }}
                disabled={busyId === certifyTarget.draftId}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-carbon-green-50 text-white hover:opacity-90 disabled:opacity-50"
              >
                {busyId === certifyTarget.draftId ? <Loader2 size={12} className="animate-spin" /> : <BadgeCheck size={12} />} Certify
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CHA-199 bulk-certify confirm — mints N legally-binding CoCs, one at a time. Explicit gate. */}
      {bulkCertifyOpen && (() => {
        const targets = liveDrafts.filter((d) => selected.has(d.draftId) && bulkEligible(d, 'certify'));
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="font-plex bg-carbon-white border-t-[3px] border-t-carbon-blue-60 border border-carbon-border max-w-md w-full p-4 space-y-3">
              <div className="flex items-center gap-2 text-carbon-ink font-semibold"><BadgeCheck size={16} className="text-carbon-green-50" /> Certify {targets.length} draft{targets.length === 1 ? '' : 's'} with CPSC</div>
              <p className="text-sm text-carbon-gray-70">
                Submits <strong>doCertify=TRUE</strong> for the <strong>{targets.length}</strong> selected draft{targets.length === 1 ? '' : 's'} that are Awaiting Certification — one at a time. Each creates a <strong>legally-binding Certificate of Conformity</strong> that cannot be rolled back. Reports: <span className="font-mono">{targets.map((d) => d.reportNo).join(', ') || '(none eligible)'}</span>.
              </p>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => setBulkCertifyOpen(false)} autoFocus className="px-3 py-1.5 text-xs border border-carbon-gray-30 text-carbon-gray-80 hover:bg-carbon-gray-10">Cancel</button>
                <button onClick={() => { setBulkCertifyOpen(false); runBulk('certify'); }} disabled={!targets.length} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-carbon-green-50 text-white hover:opacity-90 disabled:opacity-50"><BadgeCheck size={12} /> Certify {targets.length}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// One field row: label, value, provenance chip, and (if it's an outstanding needs-confirmation field)
// an inline confirm control. Confirming is per-field (anti-rubberstamp, decision #5 Medium). Scalar /
// lookup fields offer a text override; OBJECT fields (manufacturer, poc — structured parties from the
// tenant defaults) are confirm-as-is only (no text input — you can't retype a party object, and the
// backend rejects scalar overrides on them), so the operator just affirms the default is correct.
export function Field({ label, value, prov, d, field, out, call, busyId, overrides, setOverrides, placeholder, object, fieldErrors }) {
  const chip = PROV_CHIP[prov];
  const confirmable = d && field && (out || []).some(([f]) => f === field);
  const confirmed = d && field && (d.confirmations || {})[field];
  const okey = d ? `${d.draftId}|${field}` : '';
  // Guard: display-only Field rows (versionId/certificateType/name/color) render without `overrides`
  // (and without `d`, so okey==''). `overrides[okey]` was `undefined['']` → the P0 render crash. (§6.2.1)
  const ov = (overrides || {})[okey] ?? '';
  const errs = fieldErrors || [];
  return (
    <div className="py-0.5">
    <div className="flex items-center gap-2">
      <span className="font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-50 w-32 shrink-0 self-center">{label}</span>
      <span className={`truncate flex-1 ${errs.length ? 'text-carbon-red-60 font-medium' : 'text-carbon-gray-80'}`}>{value || '—'}</span>
      {chip && <Pill tone={chip.tone}>{chip.label}</Pill>}
      {confirmed && <Pill tone="green" icon={CheckCircle2}>Confirmed</Pill>}
      {confirmable && (
        <div className="flex items-center gap-1">
          {!object && (
            <input
              type="text" value={ov} placeholder={placeholder || 'value'}
              onChange={(e) => setOverrides((p) => ({ ...p, [okey]: e.target.value }))}
              className="text-xs border border-carbon-gray-30 bg-carbon-gray-10 px-1.5 py-0.5 w-28 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60"
            />
          )}
          <button
            onClick={() => {
              const args = { draftId: d.draftId, field };
              if (!object && s(ov)) args.value = field === 'labs[0].citationCodes' ? s(ov).split(',').map((x) => x.trim()).filter(Boolean) : s(ov);
              call('confirmDraftField', args, d.draftId);
            }}
            disabled={busyId === d.draftId}
            className="flex items-center gap-1 px-1.5 py-0.5 text-[11px] bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50"
            title={object ? 'Confirm this default party is correct' : 'Confirm this field (optionally with the value entered)'}
          >
            {busyId === d.draftId ? <Loader2 size={10} className="animate-spin" /> : <BadgeCheck size={10} />} Confirm
          </button>
        </div>
      )}
    </div>
      {errs.map((e, i) => (
        <div key={i} className="ml-32 pl-2 text-[11px] text-carbon-red-60 flex items-start gap-1">
          <AlertTriangle size={10} className="mt-0.5 shrink-0" /><span>{e.errorMessage}{e.errorCode ? ` [${e.errorCode}]` : ''}</span>
        </div>
      ))}
    </div>
  );
}
